import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Download, Copy, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  suggestion:  { icon: "💡", label: "Suggestion",  cls: "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]" },
  planned:     { icon: "🔵", label: "Planned",     cls: "border-[#0369A1] text-[#0369A1] bg-[#F0F9FF]" },
  in_progress: { icon: "🟡", label: "In Progress", cls: "border-[#B45309] text-[#B45309] bg-[#FFFBEB]" },
  rework:      { icon: "🔁", label: "Rework",      cls: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" },
  live:        { icon: "🚀", label: "Live",        cls: "border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]" },
  declined:    { icon: "✖", label: "Declined",    cls: "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg-2)]" },
};
const ACCEPT_TO = ["planned", "in_progress", "rework", "live"];
const REQ_MAX = 400;
const PRIORITY = {
  high: { label: "High", cls: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" },
  medium: { label: "Medium", cls: "border-[var(--warn)] text-[var(--warn)] bg-[var(--warn-bg)]" },
  low: { label: "Low", cls: "border-[var(--line)] text-[var(--ink-2)] bg-white" },
};

export default function BuildLog() {
  const [entries, setEntries] = useState([]);
  const [options, setOptions] = useState({ statuses: [], modules: [] });
  const [filters, setFilters] = useState({ status: "", module: "", date_from: "", date_to: "" });
  const [priFilter, setPriFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const r = await api.get("/build-log", { params });
    setEntries(r.data);
  }
  useEffect(() => { api.get("/build-log/options").then(r => setOptions(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.module, filters.date_from, filters.date_to]);

  async function setPriority(e, priority) {
    setEntries((xs) => xs.map((x) => (x.id === e.id ? { ...x, priority } : x)));
    try { await api.patch(`/build-log/${e.id}`, { priority }); toast.success(`${e.req_id || "Entry"} priority → ${PRIORITY[priority].label}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Priority update failed"); load(); }
  }

  async function setStatus(e, status) {
    setEntries((xs) => xs.map((x) => (x.id === e.id ? { ...x, status } : x)));
    try { await api.patch(`/build-log/${e.id}`, { status }); toast.success(`${e.req_id || "Entry"} → ${STATUS_STYLES[status].label}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Status update failed"); load(); }
  }

  async function remove(e) {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await api.delete(`/build-log/${e.id}`);
    toast.success("Deleted");
    load();
  }

  const counts = useMemo(() => {
    const c = { suggestion: 0, planned: 0, in_progress: 0, rework: 0, live: 0, declined: 0 };
    entries.forEach((e) => { if (c[e.status] != null) c[e.status]++; });
    return c;
  }, [entries]);

  function toMarkdown() {
    const rows = entries.map(e => `| ${e.req_id || "–"} | ${e.date} | ${e.module} | ${(e.request_text || e.title || "").replace(/\|/g, "/").replace(/\n/g, " ")} | ${e.requested_by || "–"} | ${STATUS_STYLES[e.status]?.label || e.status} |`);
    return `# Build Log — Finchley Business Services\n\nExported ${new Date().toISOString().slice(0, 10)}\n\n| Request ID | Date | Module | Request | Requested By | Status |\n|---|---|---|---|---|---|\n${rows.join("\n")}\n`;
  }

  function exportMd() {
    const blob = new Blob([toMarkdown()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `build-log-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function copyText() {
    try {
      await navigator.clipboard.writeText(toMarkdown());
      toast.success("Build log copied to clipboard");
    } catch (e) {
      toast.error("Could not copy");
    }
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-serif-strong">Build Log</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">Audit trail of what's been requested and what's been built.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="build-log-copy" onClick={copyText} className="btn-ghost flex items-center gap-1.5 text-sm !py-2 !px-3" title="Copy as markdown">
            <Copy size={13} /> Copy
          </button>
          <button data-testid="build-log-export" onClick={exportMd} className="btn-ghost flex items-center gap-1.5 text-sm !py-2 !px-3" title="Download as .md file">
            <Download size={13} /> Export
          </button>
          <button data-testid="build-log-new" onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2 text-sm !py-2 !px-3">
            <Plus size={14} /> New entry
          </button>
        </div>
      </div>

      {/* Count chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {Object.entries(STATUS_STYLES).filter(([k]) => k !== "declined" || filters.status === "declined").map(([k, s]) => (
          <button
            key={k}
            onClick={() => setFilters(f => ({ ...f, status: f.status === k ? "" : k }))}
            data-testid={`chip-${k}`}
            className={`text-[10.5px] px-2.5 py-0.5 rounded-sm border transition ${filters.status === k ? s.cls : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--navy)]"}`}
          >{s.icon} {s.label} — <strong>{counts[k]}</strong></button>
        ))}
        {filters.status !== "declined" && (
          <button data-testid="chip-declined" onClick={() => setFilters(f => ({ ...f, status: "declined" }))} className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] hover:text-[var(--navy)]">Show declined</button>
        )}
        {(filters.status || filters.module || filters.date_from || filters.date_to) && (
          <button onClick={() => setFilters({ status: "", module: "", date_from: "", date_to: "" })} className="text-[10px] uppercase tracking-wider text-[var(--navy)] hover:opacity-70">Clear filters</button>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim" style={{ tableLayout: "fixed", width: "100%", minWidth: 900 }}>
          <colgroup>
            <col style={{ width: 64 }} /><col style={{ width: 76 }} /><col style={{ width: 84 }} /><col />
            <col style={{ width: 108 }} /><col style={{ width: 100 }} /><col style={{ width: 84 }} /><col style={{ width: 250 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Req ID</th>
              <th>Date</th>
              <th>Module</th>
              <th>Request</th>
              <th>Requested By</th>
              <th>Status</th>
              <th>Priority</th>
              <th className="text-right">Actions</th>
            </tr>
            <tr className="bg-[var(--bg-2)]">
              <th></th>
              <th className="!py-1 !px-1.5">
                <input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="w-full text-[10px] px-1 h-[20px] border border-[var(--line)] rounded-sm bg-white font-normal" />
              </th>
              <th className="!py-1 !px-1.5">
                <select data-testid="filter-module" value={filters.module} onChange={(e) => setFilters(f => ({ ...f, module: e.target.value }))} className="w-full text-[10px] px-1 h-[20px] border border-[var(--line)] rounded-sm bg-white font-sans font-normal">
                  <option value="">All</option>
                  {options.modules.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </th>
              <th></th>
              <th></th>
              <th className="!py-1 !px-1.5">
                <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full text-[10px] px-1 h-[20px] border border-[var(--line)] rounded-sm bg-white font-sans font-normal">
                  <option value="">All</option>
                  {options.statuses.map(s => <option key={s} value={s}>{STATUS_STYLES[s]?.label || s}</option>)}
                </select>
              </th>
              <th className="!py-1 !px-1.5">
                <select data-testid="filter-priority" value={priFilter} onChange={(e) => setPriFilter(e.target.value)} className="w-full text-[10px] px-1 h-[20px] border border-[var(--line)] rounded-sm bg-white font-sans font-normal">
                  <option value="">All</option>
                  {Object.entries(PRIORITY).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                </select>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.filter((e) => !priFilter || (e.priority || "low") === priFilter).map(e => {
              const s = STATUS_STYLES[e.status] || { icon: "", label: e.status, cls: "" };
              return (
                <tr key={e.id} data-testid={`row-${e.id}`}>
                  <td className="font-mono-data text-[10.5px] whitespace-nowrap" data-testid={`req-id-${e.id}`}>{e.req_id || "—"}</td>
                  <td className="font-mono-data text-[10.5px] whitespace-nowrap">{e.date}</td>
                  <td className="truncate" title={e.module}>{e.module}</td>
                  <td>
                    <div className="whitespace-pre-line break-words" data-testid={`request-${e.id}`}>{e.request_text || e.title}</div>
                    {e.source === "suggestion" && e.decision && (
                      <div className="text-[10px] text-[var(--ink-2)] mt-0.5" data-testid={`suggested-by-${e.id}`}>
                        Suggestion · {e.decision} by {e.decided_by}
                      </div>
                    )}
                  </td>
                  <td className="truncate" title={e.requested_by} data-testid={`requested-by-${e.id}`}>{e.requested_by || "—"}</td>
                  <td>
                    {ACCEPT_TO.includes(e.status) ? (
                      <select data-testid={`status-select-${e.id}`} value={e.status} onChange={(ev) => setStatus(e, ev.target.value)}
                        className={`w-full text-[10.5px] px-1 py-0 h-[20px] rounded-sm border cursor-pointer font-medium ${s.cls}`}>
                        {ACCEPT_TO.map((k) => <option key={k} value={k}>{STATUS_STYLES[k].label}</option>)}
                      </select>
                    ) : (
                      <span data-testid={`status-pill-${e.id}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${s.cls}`}>
                        {s.label}
                      </span>
                    )}
                  </td>
                  <td>
                    <select data-testid={`priority-select-${e.id}`} value={e.priority || "low"} onChange={(ev) => setPriority(e, ev.target.value)}
                      className={`w-full text-[10.5px] px-1 py-0 h-[20px] rounded-sm border cursor-pointer font-medium ${PRIORITY[e.priority || "low"].cls}`}>
                      {Object.entries(PRIORITY).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                    </select>
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      {e.status === "suggestion" && <DecideControls entry={e} onDone={load} />}
                      <button data-testid={`edit-${e.id}`} onClick={() => setEditing(e)} className="inline-flex items-center gap-1 text-[10px] px-1.5 h-[20px] rounded-sm border border-[var(--line)] text-[var(--ink)] hover:border-[var(--navy)] hover:bg-[var(--bg-2)] transition-colors"><Pencil size={10} /> Edit</button>
                      <button data-testid={`delete-${e.id}`} onClick={() => remove(e)} title="Delete" className="inline-flex items-center px-1.5 h-[20px] rounded-sm border border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)] transition-colors"><Trash2 size={10} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {entries.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--ink-2)]">
                No entries yet. Click <b>New entry</b> to log a request.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(showNew || editing) && (
        <EntryModal
          entry={editing}
          options={options}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DecideControls({ entry, onDone }) {
  const [to, setTo] = useState("planned");
  async function accept() {
    try { await api.post(`/build-log/${entry.id}/accept`, { status: to }); toast.success(`Accepted as ${STATUS_STYLES[to].label}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Accept failed"); }
  }
  async function decline() {
    if (!window.confirm("Decline this suggestion?")) return;
    try { await api.post(`/build-log/${entry.id}/decline`); toast.success("Declined"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Decline failed"); }
  }
  return (
    <>
      <select data-testid={`accept-status-${entry.id}`} value={to} onChange={(e) => setTo(e.target.value)}
        className="text-[10px] px-0.5 h-[20px] border border-[var(--line)] rounded-sm bg-white">
        {ACCEPT_TO.map((s) => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
      </select>
      <button data-testid={`accept-${entry.id}`} onClick={accept} title="Accept"
        className="inline-flex items-center gap-0.5 text-[9.5px] uppercase px-1.5 h-[20px] rounded-sm border border-[var(--ok)] text-[var(--ok)] hover:bg-[var(--ok-bg)]"><Check size={9} /> Accept</button>
      <button data-testid={`decline-${entry.id}`} onClick={decline} title="Decline"
        className="inline-flex items-center gap-0.5 text-[9.5px] uppercase px-1.5 h-[20px] rounded-sm border border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)]"><X size={10} /></button>
    </>
  );
}

function EntryModal({ entry, options, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: entry?.date || new Date().toISOString().slice(0, 10),
    module: entry?.module || options.modules[0] || "Other",
    title: entry?.request_text || entry?.title || "",
    status: entry?.status || "planned",
  });
  const [saving, setSaving] = useState(false);
  const chars = form.title.length;

  async function save() {
    if (!form.title.trim()) { toast.error("Request is required"); return; }
    setSaving(true);
    try {
      if (entry) {
        await api.patch(`/build-log/${entry.id}`, form);
      } else {
        await api.post("/build-log", form);
      }
      toast.success(entry ? "Updated" : "Entry added");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card-flat bg-white p-6 w-full max-w-xl">
        <h2 className="text-lg font-serif-strong mb-4">{entry ? "Edit entry" : "New entry"}</h2>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs text-[var(--ink-2)] block mb-1">Date</label>
            <input data-testid="entry-date" type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input text-sm w-full" />
          </div>
          <div>
            <label className="text-xs text-[var(--ink-2)] block mb-1">Module</label>
            <select data-testid="entry-module" value={form.module} onChange={(e) => setForm(f => ({ ...f, module: e.target.value }))} className="input text-sm w-full">
              {options.modules.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs text-[var(--ink-2)] mb-1 flex items-center justify-between">
            <span>Request *</span>
            <span data-testid="entry-title-count" className={chars > REQ_MAX ? "text-[var(--err)]" : ""}>{chars}/{REQ_MAX}</span>
          </label>
          <textarea data-testid="entry-title" rows={4} maxLength={REQ_MAX} value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Describe the request and any detail needed" className="input text-sm w-full" />
        </div>
        <div className="mb-4">
          <label className="text-xs text-[var(--ink-2)] block mb-1">Status</label>
          <select data-testid="entry-status" value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="input text-sm w-full">
            {options.statuses.map(s => <option key={s} value={s}>{STATUS_STYLES[s]?.label || s}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm !py-2 !px-3">Cancel</button>
          <button data-testid="entry-save" onClick={save} disabled={saving} className="btn-primary text-sm !py-2 !px-3">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
